import { keepPreviousData, useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiResponse } from "@/shared/types/api.ts";
import { QUERY_KEYS } from "@/alerts/api/config.ts";
import {
  getAlerts,
  getAlertsList,
  getAlertsMap,
  getAlertById,
  getAlertActivities,
  createAlert,
  updateAlert,
  deleteAlert,
  IGetAlertsInput,
  ICreateAlertInput,
  IUpdateAlertInput,
} from "@/alerts/api/requests/machine-alerts.requests.ts";
import {
  Alert,
  AlertDetail,
  AlertActivityDTO,
  AlertListItem,
  AlertMapResponse,
  GetAlertsInput,
} from "@/alerts/types/machine-alert.ts";
import { applyDefaultIfEmpty } from "@/shared/utils/apply-default-if-empty.ts";

/**
 * True for the alert LIST reads an edited alert can show up in:
 * `[alerts, params]`, `[alerts, "list", input]` and `[alerts, "map", input]`.
 * Excludes alert details (`[alerts, id]`) and the operator workspace's
 * queue/history/analytics reads, which reconcile themselves — see
 * `use-alerts-live.ts`.
 */
export const isAlertListQueryKey = (queryKey: readonly unknown[]): boolean => {
  const [root, scope] = queryKey;
  if (root !== QUERY_KEYS.ALERTS) return false;
  return scope === "list" || scope === "map" || (typeof scope === "object" && scope !== null);
};

export const useGetAlerts = (
  params: IGetAlertsInput,
  options?: { refetchInterval?: number | false; gcTime?: number; keepPrevious?: boolean },
) => {
  const defaultPagination = applyDefaultIfEmpty(
    { page: params?.page, pageSize: (params as any)?.pageSize ?? params?.itemsPerPage },
    { page: 1, pageSize: 10 },
    true,
    QUERY_KEYS.ALERTS
  );

  const page = params?.page ?? defaultPagination.page;
  const pageSize = (params as any)?.pageSize ?? params?.itemsPerPage ?? defaultPagination.pageSize;

  const finalParams: IGetAlertsInput = {
    ...params,
    page,
    itemsPerPage: pageSize,
  } as IGetAlertsInput;

  return useQuery<ApiResponse<Alert[]>>({
    queryKey: [QUERY_KEYS.ALERTS, finalParams],
    queryFn: () => getAlerts(finalParams),
    refetchInterval: options?.refetchInterval ?? false,
    ...(options?.gcTime !== undefined && { gcTime: options.gcTime }),
    ...(options?.keepPrevious && { placeholderData: keepPreviousData }),
  })
}

/**
 * New Alerts contract — scoped, filtered, ordered, paginated list.
 * Keyed off the whole input so React Query refetches on any scope/filter/order
 * /page change. `enabled` lets callers hold the query until a scope is ready.
 */
export const useGetAlertsList = (input: GetAlertsInput, enabled: boolean = true) => {
  return useQuery<ApiResponse<AlertListItem[]>>({
    queryKey: [
      QUERY_KEYS.ALERTS,
      "list",
      {
        ...input,
        start_time: input.start_time.toISOString(),
        end_time: input.end_time.toISOString(),
      },
    ],
    queryFn: () => getAlertsList(input),
    enabled,
  });
};

/**
 * New Alerts contract — map items for GET /api/alerts/map/. Same scope/filters
 * as {@link useGetAlertsList} (no pagination); returns capped items + truncated.
 */
export const useGetAlertsMap = (
  input: Omit<GetAlertsInput, "limit" | "offset">,
  enabled: boolean = true,
) => {
  return useQuery<ApiResponse<AlertMapResponse>>({
    queryKey: [
      QUERY_KEYS.ALERTS,
      "map",
      {
        ...input,
        start_time: input.start_time.toISOString(),
        end_time: input.end_time.toISOString(),
      },
    ],
    queryFn: () => getAlertsMap(input),
    enabled,
  });
};

/**
 * Get alert by ID with images and preview
 * @param id - Alert ID
 * @param enabled - Enable/disable query
 * @returns Alert detail with preview image and all event images
 */
export const useGetAlertById = (id: string | number | undefined, enabled: boolean = true) => {
  // Callers hold the id either as a number (`Alert.id`) or as a string (the
  // map's selection ids). The cache key is normalized to one canonical form so
  // the same alert isn't fetched and stored twice under `123` and `"123"`.
  const alertId = id != null ? String(id) : undefined;

  return useQuery<ApiResponse<AlertDetail>>({
    queryKey: [QUERY_KEYS.ALERTS, alertId],
    queryFn: () => getAlertById(id!),
    enabled: !!id && enabled,
  })
}

/**
 * New Alerts contract — workflow activity timeline for one alert.
 * Keyed under the alert so it invalidates alongside the detail after an action.
 */
export const useGetAlertActivities = (
  id: string | number | undefined,
  enabled: boolean = true,
) => {
  return useQuery<ApiResponse<AlertActivityDTO[]>>({
    queryKey: [QUERY_KEYS.ALERTS, id, "activities"],
    queryFn: () => getAlertActivities(id!),
    enabled: !!id && enabled,
  });
};

export const useCreateAlert = () => {
  const queryClient = useQueryClient();

  return useMutation<ApiResponse<Alert>, Error, ICreateAlertInput>({
    mutationFn: (data) => createAlert(data),
    onSuccess: () => {
      // Invalidate and refetch alerts list
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ALERTS] });
    },
  });
}

export const useUpdateAlert = () => {
  const queryClient = useQueryClient();

  return useMutation<ApiResponse<Alert>, Error, { id: string; data: IUpdateAlertInput }>({
    mutationFn: ({ id, data }) => updateAlert(id, data),
    onSuccess: (_, variables) => {
      // Refetch only what this alert can appear in: its own detail and the alert
      // LIST reads. Invalidating the bare `["alerts"]` prefix would also nuke
      // every other alert's detail and the operator workspace's queue/history/
      // analytics — which reconcile themselves (see `use-alerts-live.ts`) — so
      // a single keystroke (R/F) fanned out into a request per cached alert.
      queryClient.invalidateQueries({ predicate: (query) => isAlertListQueryKey(query.queryKey) });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ALERTS, String(variables.id)] });
    },
  });
}

export const useDeleteAlert = () => {
  const queryClient = useQueryClient();

  return useMutation<ApiResponse<void>, Error, string>({
    mutationFn: (id) => deleteAlert(id),
    onSuccess: (_, id) => {
      // Invalidate and refetch alerts list and remove specific alert from cache
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ALERTS] });
      queryClient.removeQueries({ queryKey: [QUERY_KEYS.ALERTS, id] });
    },
  });
}
